/**
 * ─────────────────────────────────────────────
 * src/hooks/useProject.js
 * ─────────────────────────────────────────────
 */

import { useDispatch, useSelector } from "react-redux";
import {
  createProject,
  fetchProjectById,
  fetchProjectLists,
  fetchProjectSummary,
  addProjectMember,
  removeProjectMember,
  fetchAllUsers,
  setSelectedProject,
  clearSelectedProject,
  setColumns,
  addTaskToColumn,
  removeTaskFromColumn,
  resetCreateProject,
  clearProjectErrors,
  clearAllUsers,
  updateProject as updateProjectThunk,
  disableProject as disableProjectThunk,
  enableProject as enableProjectThunk,
} from "../store/slices/projectSlice";
import projectService from "../services/projectService";

const useProject = () => {
  const dispatch = useDispatch();

  const {
    selectedProject,
    columns,
    allUsers,
    isCreatingProject,
    isFetchingProject,
    isFetchingLists,
    isFetchingSummary,
    isAddingMember,
    isRemovingMember,
    isFetchingUsers,
    createProjectError,
    projectError,
    listsError,
    membersError,
    createProjectSuccess,
    isUpdatingProject,
     updateProjectError,
  } = useSelector((state) => state.project);

  // ── CSV export (not a Redux thunk — it triggers a file download) ──
  const exportCSV = async (projectId) => {
    try {
      const response = await projectService.exportActivityCSV(projectId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "project_tasks.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
    }
  };

  return {
    // ── State ──────────────────────────────
    selectedProject,
    columns,
    allUsers,
    createProjectSuccess,

    // ── Loading ────────────────────────────
    isCreatingProject,
    isFetchingProject,
    isFetchingLists,
    isFetchingSummary,
    isAddingMember,
    isRemovingMember,
    isFetchingUsers,
    isUpdatingProject,

    // ── Errors ─────────────────────────────
    createProjectError,
    projectError,
    listsError,
    membersError,
    updateProjectError,

    // ── Actions ────────────────────────────
    makeProject: (data) => dispatch(createProject(data)),
    loadProject: (id) => dispatch(fetchProjectById(id)),
    loadProjectLists: (id) => dispatch(fetchProjectLists(id)),
    loadProjectSummary: (id) => dispatch(fetchProjectSummary(id)),
    addMember: (projectId, userId) =>
      dispatch(addProjectMember({ projectId, userId })),
    removeMember: (projectId, userId) =>
      dispatch(removeProjectMember({ projectId, userId })),
    loadAllUsers: (picker = false) => dispatch(fetchAllUsers(picker)),
    selectProject: (project) => dispatch(setSelectedProject(project)),
    deselectProject: () => dispatch(clearSelectedProject()),
    updateColumns: (cols) => dispatch(setColumns(cols)),
    addTask: (listId, task) => dispatch(addTaskToColumn({ listId, task })),
    removeTask: (listId, taskId) =>
      dispatch(removeTaskFromColumn({ listId, taskId })),
    resetCreate: () => dispatch(resetCreateProject()),
    clearErrors: () => dispatch(clearProjectErrors()),
    clearUsers: () => dispatch(clearAllUsers()),
    
exportCSV,
    updateProject: (projectId, updates) =>
      dispatch(updateProjectThunk({ projectId, updates })).unwrap(),
    disableProject: (projectId) => dispatch(disableProjectThunk(projectId)),
    enableProject: (projectId) => dispatch(enableProjectThunk(projectId)),
  };
};

export default useProject;
